import React, {
  FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Button, Divider, Input, message, Select, Spin } from 'antd';
import { MdSend } from 'react-icons/md';
import TextArea from 'antd/lib/input/TextArea';

import Layout from '../../components/Layout';

import { Container, AddForm } from './styles';
import api from '../../services/api';

interface User {
  _id: string;
  firstName: string;
  lastName?: string;
  email: string;
}

const Notifications: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

  const titleRef = useRef<Input>(null);
  const subtitleRef = useRef<Input>(null);
  const bodyRef = useRef<TextArea>(null);

  useEffect(() => {
    async function loadUsers(): Promise<void> {
      setLoading(true);
      try {
        const response = await api.get('/users');
        setUsers(response.data);
      } catch (err) {
        message.error('Falha ao carregar usuários');
      } finally {
        setLoading(false);
      }
    }

    loadUsers();
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(user => user._id));
    }
  }, [users, selectedUsers]);

  const handleSubmit = useCallback(
    async (e: FormEvent): Promise<void> => {
      e.preventDefault();

      const title = titleRef.current?.input.value;
      const subtitle = subtitleRef.current?.input.value;
      const body = bodyRef.current?.state.value;

      if (!title) {
        message.warning('Preencha o título');
        return;
      }

      if (!subtitle) {
        message.warning('Preencha o subtítulo');
        return;
      }

      if (selectedUsers.length === 0) {
        message.warning('Selecione pelo menos um usuário');
        return;
      }

      setSending(true);

      try {
        const response = await api.post('/notifications/broadcast', {
          title,
          subtitle,
          body: body || '',
          userIds: selectedUsers,
        });

        message.success(
          `Notificação enviada! ${response.data.sent} push(es) enviado(s), ${response.data.saved} salva(s) no banco.`,
        );

        if (titleRef.current) titleRef.current.input.value = '';
        if (subtitleRef.current) subtitleRef.current.input.value = '';
        setSelectedUsers([]);
      } catch (err) {
        message.error('Erro ao enviar notificação');
      } finally {
        setSending(false);
      }
    },
    [selectedUsers],
  );

  if (loading) {
    return (
      <Layout title="Notificações">
        <Spin style={{ marginTop: '50%' }} size="large" />
      </Layout>
    );
  }

  return (
    <Layout title="Notificações">
      <Container>
        <div className="title-group">
          <h2>Enviar Notificação Push</h2>
        </div>

        <AddForm onSubmit={handleSubmit}>
          <div className="input-group">
            <h4>Título</h4>
            <Input
              ref={titleRef}
              type="text"
              placeholder="Título da notificação"
            />
          </div>

          <div className="input-group">
            <h4>Subtítulo</h4>
            <Input
              ref={subtitleRef}
              type="text"
              placeholder="Subtítulo da notificação"
            />
          </div>

          <div className="input-group">
            <h4>Corpo (opcional)</h4>
            <TextArea
              ref={bodyRef}
              rows={4}
              placeholder="Texto adicional da notificação"
            />
          </div>

          <Divider />

          <div className="input-group">
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <h4>Usuários</h4>
              <Button type="link" onClick={handleSelectAll}>
                {selectedUsers.length === users.length
                  ? 'Desmarcar todos'
                  : 'Selecionar todos'}
              </Button>
            </div>
            <Select
              mode="multiple"
              placeholder="Selecione os usuários"
              value={selectedUsers}
              onChange={(value: string[]) => setSelectedUsers(value)}
              optionFilterProp="children"
              showSearch
              style={{ width: '100%' }}
            >
              {users.map(user => (
                <Select.Option key={user._id} value={user._id}>
                  {user.firstName} {user.lastName || ''} - {user.email}
                </Select.Option>
              ))}
            </Select>
          </div>

          <Divider />

          <div className="button-group">
            <Button
              icon={<MdSend />}
              type="primary"
              htmlType="submit"
              loading={sending}
            >
              Enviar Notificação
            </Button>
          </div>
        </AddForm>
      </Container>
    </Layout>
  );
};

export default Notifications;
